/**
 * V2.0 Phase 4a — Admin > Mortgage Config.
 *
 * Edit the singleton `mortgage_engine_config`:
 *   - Purchase power assumptions (interest rate, tax, insurance, PMI, HOA,
 *     DTI ceilings, term).
 *   - Score weights (5 buckets, MUST sum to 100 — server validates on PATCH).
 *
 * Wave 6 Batch 3 · Cluster C — full chrome sweep onto `--ds-*` tokens +
 * `admin/_primitives` (Section / TextInput / AccentButton / GhostButton).
 * Every testid + business threshold + API payload preserved byte-exact.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Section, TextInput, AccentButton, GhostButton } from "@/admin/_primitives";

const ASSUMPTION_FIELDS = [
  { key: "interest_rate_pct", label: "Interest rate (%)", step: 0.05 },
  { key: "loan_term_years", label: "Loan term (years)", step: 1, integer: true },
  { key: "property_tax_rate_pct", label: "Property tax rate (% / yr)", step: 0.05 },
  { key: "homeowners_insurance_rate_pct", label: "Homeowners insurance (% / yr)", step: 0.05 },
  { key: "pmi_rate_pct", label: "PMI rate (% of loan / yr)", step: 0.05 },
  { key: "pmi_ltv_threshold_pct", label: "PMI LTV threshold (%)", step: 1 },
  { key: "hoa_monthly_default_usd", label: "HOA monthly default ($)", step: 5 },
  { key: "front_end_dti_ceiling_pct", label: "Front-end DTI ceiling (%)", step: 0.5 },
  { key: "back_end_dti_ceiling_pct", label: "Back-end DTI ceiling (%)", step: 0.5 },
  { key: "jumbo_threshold_usd", label: "Jumbo threshold ($)", step: 1000, integer: true },
];

const WEIGHT_FIELDS = [
  { key: "credit_profile", label: "Credit profile" },
  { key: "dti", label: "Debt-to-income" },
  { key: "down_payment", label: "Down payment" },
  { key: "employment_stability", label: "Employment stability" },
  { key: "reserves_history", label: "Reserves & housing history" },
];

// V2.0 Phase 4b — projection constants surfaced on the /opportunities page.
const FORECAST_FIELDS = [
  { key: "projected_credit_score_bump", label: "Projected credit-score bump (pts)", step: 1 },
  { key: "projected_funding_readiness_bump", label: "Projected funding readiness bump (pts)", step: 1 },
  { key: "projected_mortgage_readiness_bump", label: "Projected mortgage readiness bump (pts)", step: 1 },
  { key: "projected_partner_count_bump_when_credit_below", label: "Partner-bump credit threshold", step: 1 },
  { key: "projected_partner_count_delta", label: "Partner-bump partner delta", step: 1 },
  { key: "purchase_power_forecast_multiplier", label: "Purchase-power forecast multiplier", step: 0.01 },
];

export default function AdminMortgageConfig() {
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/mortgage-config");
      setInitial(data);
      setForm(JSON.parse(JSON.stringify(data)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const weightSum = useMemo(() => {
    if (!form?.score_weights) return 0;
    return WEIGHT_FIELDS.reduce((acc, f) => acc + Number(form.score_weights[f.key] || 0), 0);
  }, [form]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);

  function updAssumption(k, v, integer) {
    setForm((f) => ({
      ...f,
      assumptions: {
        ...f.assumptions,
        [k]: v === "" ? "" : (integer ? Math.round(Number(v)) : Number(v)),
      },
    }));
  }
  function updWeight(k, v) {
    setForm((f) => ({
      ...f,
      score_weights: {
        ...f.score_weights,
        [k]: v === "" ? "" : Math.max(0, Math.round(Number(v))),
      },
    }));
  }
  function updForecast(k, v, step) {
    setForm((f) => ({
      ...f,
      forecast_assumptions: {
        ...(f.forecast_assumptions || {}),
        [k]: v === "" ? "" : (step && step < 1 ? Number(v) : Math.round(Number(v))),
      },
    }));
  }

  async function save() {
    if (weightSum !== 100) {
      toast.error(`Weights must sum to 100 (currently ${weightSum}).`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        assumptions: form.assumptions,
        score_weights: form.score_weights,
      };
      if (form.forecast_assumptions) {
        payload.forecast_assumptions = form.forecast_assumptions;
      }
      const { data } = await api.patch("/admin/mortgage-config", payload);
      setInitial(data);
      setForm(JSON.parse(JSON.stringify(data)));
      toast.success("Mortgage config saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading || !form) {
    return (
      <AdminLayout title="Mortgage config">
        <div className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Mortgage config"
      subtitle="Purchase-power assumptions and scoring weights for the Mortgage Readiness engine"
      actions={
        <div className="flex items-center gap-2">
          <GhostButton
            data-testid="mortgage-config-revert"
            onClick={() => setForm(JSON.parse(JSON.stringify(initial)))}
            disabled={!dirty || saving}
          >
            <RefreshCw className="w-3 h-3" /> Revert
          </GhostButton>
          <AccentButton
            data-testid="mortgage-config-save"
            onClick={save}
            disabled={!dirty || saving || weightSum !== 100}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : "Save changes"}
          </AccentButton>
        </div>
      }
    >
      <AdminPageHelp
        title="Mortgage Config"
        purpose="Controls the assumptions and scoring weights used by the Mortgage Readiness engine that powers the /mortgage-intake and /mortgage-readiness surfaces."
        configures="Purchase-power assumptions (interest rate, property tax rate, insurance, PMI, HOA fees, closing costs). Score component weights (must sum to 100)."
        impact="Every mortgage-readiness result across the platform is recomputed with the new numbers on next user request. Existing historical assessment records are NOT overwritten."
        safety="Yes. Revert drops any unsaved edits, and score weights that don't sum to 100 cannot be saved. Assumption bounds are enforced by the model layer."
      />
      <div className="space-y-6 max-w-4xl">
        <Section title="Purchase power assumptions" testid="mortgage-config-section-assumptions">
          <div className="grid md:grid-cols-2 gap-4">
            {ASSUMPTION_FIELDS.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                testId={`mortgage-config-assumption-${f.key}`}
                value={form.assumptions?.[f.key] ?? ""}
                onChange={(v) => updAssumption(f.key, v, f.integer)}
                step={f.step}
              />
            ))}
          </div>
        </Section>
        <Section
          title="Score weights"
          testid="mortgage-config-section-weights"
          badge={(
            <span
              data-testid="mortgage-config-weight-sum"
              className="text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 border"
              style={{
                background: weightSum === 100 ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
                color: weightSum === 100 ? "var(--ds-color-success)" : "var(--ds-color-warning)",
                borderColor: weightSum === 100 ? "var(--ds-color-success)" : "var(--ds-color-warning)",
              }}
            >
              Sum: {weightSum} / 100
            </span>
          )}
        >
          <div className="grid md:grid-cols-2 gap-4">
            {WEIGHT_FIELDS.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                testId={`mortgage-config-weight-${f.key}`}
                value={form.score_weights?.[f.key] ?? ""}
                onChange={(v) => updWeight(f.key, v)}
                step={1}
              />
            ))}
          </div>
        </Section>

        <Section title="Forecast projections" testid="mortgage-config-section-forecast">
          <div
            className="rounded-[var(--ds-radius-card)] border px-3 py-2 text-[11px] mb-3"
            style={{
              borderColor: "var(--ds-color-admin-border)",
              background: "var(--ds-color-accent-soft)",
              color: "var(--ds-color-ink-muted)",
            }}
            data-testid="mortgage-config-forecast-note"
          >
            These constants control the &ldquo;if you complete your top recommendations&rdquo;
            projections on the Opportunities dashboard. Keep conservative for compliance safety —
            never promise a specific credit-score jump or funding outcome to a user.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {FORECAST_FIELDS.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                testId={`mortgage-config-forecast-${f.key}`}
                value={form.forecast_assumptions?.[f.key] ?? ""}
                onChange={(v) => updForecast(f.key, v, f.step)}
                step={f.step}
              />
            ))}
          </div>
        </Section>

        <div
          className="text-xs pt-4 border-t"
          style={{
            color: "var(--ds-color-ink-muted)",
            borderColor: "var(--ds-color-admin-border)",
          }}
        >
          Last updated: {form?.updated_at ? new Date(form.updated_at).toLocaleString() : "—"}
        </div>
      </div>
    </AdminLayout>
  );
}


function Field({ label, value, onChange, step = 1, testId }) {
  return (
    <label className="block">
      <span
        className="block mb-1 uppercase tracking-[0.12em] text-[10px] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      <TextInput
        type="number"
        data-testid={testId}
        value={value ?? ""}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        className="w-full tabular-nums"
      />
    </label>
  );
}
